import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  useEffect, FunctionComponent } from 'react';
import { User } from 'src/types/user.types';
import { getOnboardingData } from 'src/utils/onboardingStorage';

export interface UserContextType {
  me: User;
}

export const UserContext = createContext<UserContextType | null>(null);

type Props = PropsWithChildren<{ me: User }>;

const UserContextProvider: FunctionComponent<Props> = ({
  children,
  me,
}) => {
  const [, setStorageVersion] = useState(0);
  const storedOnboardingData = JSON.stringify(getOnboardingData(me.id));

  useEffect(() => {
    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail?.userId === me.id) {
        setStorageVersion(v => v + 1);
      }
    };

    window.addEventListener(
      'onboardingDataChanged',
      handleCustomStorageChange as EventListener,
    );

    return () => {
      window.removeEventListener(
        'onboardingDataChanged',
        handleCustomStorageChange as EventListener,
      );
    };
  }, [me.id]);

  const enhancedUser = useMemo(() => {
    const onboardingData: ReturnType<typeof getOnboardingData> =
      JSON.parse(storedOnboardingData);

    if (!onboardingData) {
      return me;
    }

    const enhanced: User = { ...me };

    if (!enhanced.firstname && onboardingData.firstname) {
      enhanced.firstname = onboardingData.firstname;
    }

    if (!enhanced.family_member_type && onboardingData.family_member_type) {
      enhanced.family_member_type = onboardingData.family_member_type;
    }

    if (!enhanced.school_level && onboardingData.school_level) {
      enhanced.school_level = onboardingData.school_level;
    }

    if (
      !enhanced.education_level_code &&
      onboardingData.education_level_code
    ) {
      enhanced.education_level_code = onboardingData.education_level_code;
    }

    if (
      (!enhanced.objectives || enhanced.objectives.length === 0) &&
      onboardingData.objectives
    ) {
      enhanced.objectives = onboardingData.objectives;
    }

    return enhanced;
  }, [me, storedOnboardingData]);

  const value = useMemo(() => ({ me: enhancedUser }), [enhancedUser]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('userContext must be used within a UserContext');
  }
  return userContext;
};

export default UserContextProvider;
